// Azzurro Italian data + conjugation engines.
// Rebuilt from the ground up for Italian morphology (the original app was
// Spanish). Single source of truth: the front-end and the PocketBase seeder
// both import `conjugate` / `conjugateSubj` / `imperative` / `participles`
// from here, so the database can never drift from what the app renders.
//
// Italian specifics handled here that Spanish does not have:
//   • three conjugations -are / -ere / -ire, plus the -isco ("inchoative")
//     sub-pattern of -ire verbs (finire → finisco);
//   • auxiliary selection (essere vs avere) for the compound tenses;
//   • passato remoto (the "preterite" slot) with its heavily irregular,
//     "1-3-3" pattern for -ere verbs (io / lui / loro irregular, tu / noi / voi
//     regular);
//   • congiuntivo presente + imperfetto (no -ra/-se split);
//   • irregular participi passati (fatto, detto, preso, visto, …).

// ============================================================
// VERB DATA
// ============================================================
// Top verbs carry an emoji, an English gloss, a `group`, and a teaching note.
// group ∈ "regular" | "irregular" | "isco" | "reflexive".
const TOP_50_VERBS = [
  { inf: "essere", en: "to be", emoji: "🌟", group: "irregular", note: "Identity, essence; auxiliary for many intransitives" },
  { inf: "avere", en: "to have", emoji: "🤲", group: "irregular", note: "Possession; the main compound-tense auxiliary" },
  { inf: "fare", en: "to do / to make", emoji: "🔨", group: "irregular", note: "Weather & idioms: fa freddo, fare una domanda" },
  { inf: "dire", en: "to say / to tell", emoji: "💬", group: "irregular", note: "Irregular present: dico, participio detto" },
  { inf: "potere", en: "to be able / can", emoji: "💪", group: "irregular", note: "posso, puoi, può…" },
  { inf: "volere", en: "to want", emoji: "❤️", group: "irregular", note: "voglio, vuoi, vuole…" },
  { inf: "dovere", en: "to have to / must", emoji: "📋", group: "irregular", note: "devo, devi, deve…" },
  { inf: "sapere", en: "to know (facts)", emoji: "🧠", group: "irregular", note: "so, sai, sa; vs conoscere (people/places)" },
  { inf: "stare", en: "to stay / to be (state)", emoji: "📍", group: "irregular", note: "Health & location; stare + gerundio = ongoing action" },
  { inf: "andare", en: "to go", emoji: "🚶", group: "irregular", note: "vado, vai, va; auxiliary essere" },
  { inf: "venire", en: "to come", emoji: "👈", group: "irregular", note: "vengo, vieni; auxiliary essere" },
  { inf: "dare", en: "to give", emoji: "🎁", group: "irregular", note: "do, dai, dà; passato remoto diedi" },
  { inf: "vedere", en: "to see", emoji: "👁️", group: "irregular", note: "Future vedrò; participio visto" },
  { inf: "parlare", en: "to speak / to talk", emoji: "🗣️", group: "regular", note: "Model regular -are verb" },
  { inf: "trovare", en: "to find", emoji: "🔍", group: "regular", note: "Regular -are; trovarsi = to be located" },
  { inf: "lasciare", en: "to leave / to let", emoji: "👋", group: "regular", note: "-ciare verb: drops the i before e/i (lascerò)" },
  { inf: "prendere", en: "to take", emoji: "✊", group: "irregular", note: "Passato remoto presi; participio preso" },
  { inf: "mettere", en: "to put", emoji: "📥", group: "irregular", note: "Passato remoto misi; participio messo" },
  { inf: "sentire", en: "to hear / to feel", emoji: "👂", group: "regular", note: "Regular -ire; sentirsi = to feel (health)" },
  { inf: "credere", en: "to believe", emoji: "🙏", group: "regular", note: "Model regular -ere verb" },
  { inf: "tornare", en: "to return", emoji: "🔄", group: "regular", note: "Regular -are; auxiliary essere" },
  { inf: "tenere", en: "to hold / to keep", emoji: "✋", group: "irregular", note: "tengo, tieni; future terrò" },
  { inf: "capire", en: "to understand", emoji: "💡", group: "isco", note: "-isco verb: capisco, capisci, capisce…" },
  { inf: "restare", en: "to stay / to remain", emoji: "🪑", group: "regular", note: "Regular -are; auxiliary essere" },
  { inf: "portare", en: "to bring / to carry / to wear", emoji: "🎒", group: "regular", note: "Regular -are" },
  { inf: "pensare", en: "to think", emoji: "💭", group: "regular", note: "Regular -are; pensare a / pensare di" },
  { inf: "passare", en: "to pass / to spend (time)", emoji: "⏭️", group: "regular", note: "Auxiliary varies: essere (go by) / avere (spend)" },
  { inf: "conoscere", en: "to know (people/places)", emoji: "🤝", group: "irregular", note: "Passato remoto conobbi; participio conosciuto" },
  { inf: "chiamare", en: "to call", emoji: "📞", group: "regular", note: "chiamarsi = to be named" },
  { inf: "aspettare", en: "to wait (for)", emoji: "⏳", group: "regular", note: "Regular -are; no preposition (aspetto l'autobus)" },
  { inf: "arrivare", en: "to arrive", emoji: "🛬", group: "regular", note: "Regular -are; auxiliary essere" },
  { inf: "cercare", en: "to look for", emoji: "🔎", group: "regular", note: "-care verb: adds h before e/i (cerchi, cercherò)" },
  { inf: "entrare", en: "to enter", emoji: "🚪", group: "regular", note: "Regular -are; auxiliary essere" },
  { inf: "ricordare", en: "to remember", emoji: "🧷", group: "regular", note: "ricordarsi di = to remember to" },
  { inf: "finire", en: "to finish", emoji: "🏁", group: "isco", note: "-isco verb: finisco, finisci, finisce…" },
  { inf: "scrivere", en: "to write", emoji: "✍️", group: "irregular", note: "Passato remoto scrissi; participio scritto" },
  { inf: "guardare", en: "to watch / to look at", emoji: "👀", group: "regular", note: "Regular -are; no preposition" },
  { inf: "chiedere", en: "to ask (for)", emoji: "🙋", group: "irregular", note: "Passato remoto chiesi; participio chiesto" },
  { inf: "rimanere", en: "to remain / to stay", emoji: "🧍", group: "irregular", note: "rimango; future rimarrò; auxiliary essere" },
  { inf: "diventare", en: "to become", emoji: "🦋", group: "regular", note: "Regular -are; auxiliary essere" },
  { inf: "uscire", en: "to go out / to exit", emoji: "🚶‍♂️", group: "irregular", note: "esco, esci, esce; auxiliary essere" },
  { inf: "parere", en: "to seem", emoji: "🤔", group: "irregular", note: "paio, pari, pare; auxiliary essere" },
  { inf: "vivere", en: "to live", emoji: "🌱", group: "irregular", note: "Passato remoto vissi; participio vissuto" },
  { inf: "mangiare", en: "to eat", emoji: "🍽️", group: "regular", note: "-giare verb: drops the i before e/i (mangerò)" },
  { inf: "leggere", en: "to read", emoji: "📖", group: "irregular", note: "Passato remoto lessi; participio letto" },
  { inf: "bere", en: "to drink", emoji: "🥤", group: "irregular", note: "Stem bev-: bevo; future berrò; participio bevuto" },
  { inf: "chiudere", en: "to close", emoji: "🔒", group: "irregular", note: "Passato remoto chiusi; participio chiuso" },
  { inf: "aprire", en: "to open", emoji: "🔓", group: "irregular", note: "Regular present; participio aperto" },
  { inf: "piacere", en: "to be pleasing / to like", emoji: "👍", group: "irregular", note: "Works like gustar: mi piace / mi piacciono" },
  { inf: "lavorare", en: "to work", emoji: "💼", group: "regular", note: "Model regular -are verb" },
  { inf: "studiare", en: "to study", emoji: "📚", group: "regular", note: "-iare verb: single i (studi, studiamo)" },
  { inf: "dormire", en: "to sleep", emoji: "😴", group: "regular", note: "Model regular -ire verb (non-isco)" },
  { inf: "partire", en: "to leave / to depart", emoji: "🧳", group: "regular", note: "Regular -ire; auxiliary essere" },
  { inf: "preferire", en: "to prefer", emoji: "⭐", group: "isco", note: "-isco verb: preferisco, preferisci…" },
];

// Additional verbs — [infinitive, english, group]. The engine conjugates these
// from the regular patterns (or the IRREGULAR_FORMS map for irregular ones).
// This list seeds the searchable dictionary; it grows over time.
const ADDITIONAL_VERBS = [
  ["abitare","to live / to reside","regular"],["accendere","to turn on / to light","irregular"],
  ["accettare","to accept","regular"],["accompagnare","to accompany","regular"],
  ["aggiungere","to add","irregular"],["aiutare","to help","regular"],
  ["alzare","to raise / to lift","regular"],["amare","to love","regular"],
  ["ascoltare","to listen (to)","regular"],["aumentare","to increase","regular"],
  ["baciare","to kiss","regular"],["ballare","to dance","regular"],
  ["bastare","to be enough","regular"],["bisognare","to be necessary","regular"],
  ["cadere","to fall","irregular"],["cambiare","to change","regular"],
  ["camminare","to walk","regular"],["cantare","to sing","regular"],
  ["capitare","to happen","regular"],["chiacchierare","to chat","regular"],
  ["cominciare","to begin","regular"],["comprare","to buy","regular"],
  ["comprendere","to understand / to comprise","irregular"],["concludere","to conclude","irregular"],
  ["consigliare","to advise","regular"],["continuare","to continue","regular"],
  ["controllare","to check / to control","regular"],["correre","to run","irregular"],
  ["costare","to cost","regular"],["costruire","to build","isco"],
  ["creare","to create","regular"],["crescere","to grow","irregular"],
  ["cucinare","to cook","regular"],["decidere","to decide","irregular"],
  ["dedicare","to dedicate","regular"],["descrivere","to describe","irregular"],
  ["desiderare","to wish / to desire","regular"],["dimenticare","to forget","regular"],
  ["dipingere","to paint","irregular"],["dirigere","to direct","irregular"],
  ["discutere","to discuss","irregular"],["disegnare","to draw","regular"],
  ["dispiacere","to be sorry / to mind","irregular"],["dividere","to divide","irregular"],
  ["domandare","to ask","regular"],["dubitare","to doubt","regular"],
  ["durare","to last","regular"],["esistere","to exist","irregular"],
  ["esprimere","to express","irregular"],["evitare","to avoid","regular"],
  ["fermare","to stop","regular"],["festeggiare","to celebrate","regular"],
  ["fingere","to pretend","irregular"],["firmare","to sign","regular"],
  ["fornire","to supply","isco"],["frequentare","to attend","regular"],
  ["fumare","to smoke","regular"],["funzionare","to work / to function","regular"],
  ["giocare","to play (game)","regular"],["girare","to turn / to spin","regular"],
  ["giungere","to reach / to arrive","irregular"],["godere","to enjoy","regular"],
  ["guadagnare","to earn","regular"],["guarire","to heal / to recover","isco"],
  ["guidare","to drive","regular"],["immaginare","to imagine","regular"],
  ["imparare","to learn","regular"],["incontrare","to meet","regular"],
  ["indicare","to indicate","regular"],["iniziare","to start","regular"],
  ["innamorarsi","to fall in love","reflexive"],["insegnare","to teach","regular"],
  ["insistere","to insist","irregular"],["interessare","to interest","regular"],
  ["invitare","to invite","regular"],["lavare","to wash","regular"],
  ["lavarsi","to wash oneself","reflexive"],["liberare","to free","regular"],
  ["litigare","to argue","regular"],["mancare","to miss / to be lacking","regular"],
  ["mandare","to send","regular"],["mantenere","to maintain","irregular"],
  ["meritare","to deserve","regular"],["migliorare","to improve","regular"],
  ["morire","to die","irregular"],["mostrare","to show","regular"],
  ["muovere","to move","irregular"],["nascere","to be born","irregular"],
  ["nascondere","to hide","irregular"],["nuotare","to swim","regular"],
  ["occupare","to occupy","regular"],["offrire","to offer","irregular"],
  ["ordinare","to order","regular"],["organizzare","to organize","regular"],
  ["pagare","to pay","regular"],["parcheggiare","to park","regular"],
  ["partecipare","to participate","regular"],["perdere","to lose","irregular"],
  ["permettere","to allow","irregular"],["pesare","to weigh","regular"],
  ["piangere","to cry","irregular"],["piovere","to rain","irregular"],
  ["porre","to place / to put","irregular"],["possedere","to own","irregular"],
  ["pranzare","to have lunch","regular"],["preoccuparsi","to worry","reflexive"],
  ["preparare","to prepare","regular"],["presentare","to introduce / to present","regular"],
  ["prestare","to lend","regular"],["produrre","to produce","irregular"],
  ["promettere","to promise","irregular"],["proporre","to propose","irregular"],
  ["provare","to try / to feel","regular"],["pulire","to clean","isco"],
  ["raccogliere","to gather / to pick","irregular"],["raccontare","to tell / to narrate","regular"],
  ["raggiungere","to reach","irregular"],["reagire","to react","isco"],
  ["regalare","to give (as a gift)","regular"],["rendere","to render / to give back","irregular"],
  ["resistere","to resist","irregular"],["restituire","to give back","isco"],
  ["riconoscere","to recognize","irregular"],["ridere","to laugh","irregular"],
  ["ridurre","to reduce","irregular"],["rifiutare","to refuse","regular"],
  ["riflettere","to reflect","regular"],["ringraziare","to thank","regular"],
  ["ripetere","to repeat","regular"],["riposare","to rest","regular"],
  ["risolvere","to solve","irregular"],["rispondere","to answer","irregular"],
  ["ritornare","to return","regular"],["riunire","to reunite","isco"],
  ["riuscire","to succeed / to manage","irregular"],["salire","to go up / to climb","irregular"],
  ["saltare","to jump","regular"],["salutare","to greet","regular"],
  ["sbagliare","to make a mistake","regular"],["scappare","to escape","regular"],
  ["scegliere","to choose","irregular"],["scendere","to go down","irregular"],
  ["scherzare","to joke","regular"],["scoprire","to discover","irregular"],
  ["scusare","to excuse","regular"],["sedere","to sit","irregular"],
  ["seguire","to follow","regular"],["sembrare","to seem","regular"],
  ["sentirsi","to feel (health/emotion)","reflexive"],["servire","to serve / to be needed","regular"],
  ["significare","to mean","regular"],["smettere","to stop (doing)","irregular"],
  ["soffrire","to suffer","irregular"],["sognare","to dream","regular"],
  ["sorridere","to smile","irregular"],["sostenere","to support / to sustain","irregular"],
  ["spegnere","to turn off","irregular"],["spendere","to spend (money)","irregular"],
  ["sperare","to hope","regular"],["spiegare","to explain","regular"],
  ["spingere","to push","irregular"],["sposare","to marry","regular"],
  ["spostare","to move / to shift","regular"],["stringere","to tighten / to squeeze","irregular"],
  ["succedere","to happen","irregular"],["suonare","to play (instrument) / to ring","regular"],
  ["superare","to overcome / to pass","regular"],["svegliare","to wake (someone)","regular"],
  ["svegliarsi","to wake up","reflexive"],["svolgere","to carry out","irregular"],
  ["telefonare","to phone","regular"],["temere","to fear","regular"],
  ["tirare","to pull / to throw","regular"],["toccare","to touch","regular"],
  ["togliere","to remove","irregular"],["tradurre","to translate","irregular"],
  ["trascorrere","to spend (time)","irregular"],["usare","to use","regular"],
  ["vendere","to sell","regular"],["vestirsi","to get dressed","reflexive"],
  ["viaggiare","to travel","regular"],["vincere","to win","irregular"],
  ["visitare","to visit","regular"],["volare","to fly","regular"],
  ["voltare","to turn","regular"],
  // --- expansion batch (all regular unless noted; engine conjugates them) ---
  ["accadere","to happen","irregular"],["accorgersi","to notice","reflexive"],
  ["addormentarsi","to fall asleep","reflexive"],["affittare","to rent","regular"],
  ["agire","to act","isco"],["allenare","to train","regular"],
  ["annoiarsi","to get bored","reflexive"],["apparire","to appear","irregular"],
  ["applaudire","to applaud","regular"],["apprezzare","to appreciate","regular"],
  ["arrabbiarsi","to get angry","reflexive"],["arredare","to furnish","regular"],
  ["asciugare","to dry","regular"],["aspirare","to aspire / to vacuum","regular"],
  ["assaggiare","to taste","regular"],["assistere","to attend / to assist","irregular"],
  ["assumere","to hire / to assume","irregular"],["attaccare","to attack / to attach","regular"],
  ["attraversare","to cross","regular"],["avvicinarsi","to approach","reflexive"],
  ["avvisare","to warn / to notify","regular"],["avvenire","to occur","irregular"],
  ["badare","to look after","regular"],["bagnare","to wet","regular"],
  ["bocciare","to fail / to reject","regular"],["bollire","to boil","regular"],
  ["brindare","to toast","regular"],["bruciare","to burn","regular"],
  ["bussare","to knock","regular"],["buttare","to throw away","regular"],
  ["calare","to lower / to drop","regular"],["calcolare","to calculate","regular"],
  ["caricare","to load / to charge","regular"],["celebrare","to celebrate","regular"],
  ["cenare","to have dinner","regular"],["chiarire","to clarify","isco"],
  ["circondare","to surround","regular"],["coltivare","to cultivate","regular"],
  ["comandare","to command","regular"],["combattere","to fight","regular"],
  ["compilare","to fill out","regular"],["completare","to complete","regular"],
  ["complicare","to complicate","regular"],["comporre","to compose","irregular"],
  ["comportarsi","to behave","reflexive"],["concedere","to grant","irregular"],
  ["condividere","to share","irregular"],["confermare","to confirm","regular"],
  ["confondere","to confuse","irregular"],["congelare","to freeze","regular"],
  ["consegnare","to deliver","regular"],["conservare","to preserve","regular"],
  ["considerare","to consider","regular"],["consumare","to consume","regular"],
  ["convincere","to convince","irregular"],["coprire","to cover","irregular"],
  ["correggere","to correct","irregular"],["corrispondere","to correspond","irregular"],
  ["coricarsi","to lie down","reflexive"],["cucire","to sew","regular"],
  ["curare","to cure / to look after","regular"],["danneggiare","to damage","regular"],
  ["decollare","to take off","regular"],["definire","to define","isco"],
  ["depositare","to deposit","regular"],["digitare","to type","regular"],
  ["dimagrire","to lose weight","isco"],["dimostrare","to demonstrate","regular"],
  ["dipendere","to depend","irregular"],["disturbare","to disturb","regular"],
  ["divertirsi","to have fun","reflexive"],["donare","to donate","regular"],
  ["esagerare","to exaggerate","regular"],["esaminare","to examine","regular"],
  ["escludere","to exclude","irregular"],["eseguire","to carry out","isco"],
  ["esitare","to hesitate","regular"],["evadere","to evade","irregular"],
  ["fallire","to fail","isco"],["fasciare","to bandage","regular"],
  ["fidarsi","to trust","reflexive"],["fissare","to fix / to stare","regular"],
  ["forzare","to force","regular"],["fregare","to rub / to cheat","regular"],
  ["friggere","to fry","irregular"],["gestire","to manage","isco"],
  ["gettare","to throw","regular"],["gridare","to shout","regular"],
  ["gustare","to taste / to savour","regular"],["illuminare","to light up","regular"],
  ["impedire","to prevent","isco"],["impegnarsi","to commit oneself","reflexive"],
  ["importare","to import / to matter","regular"],["indossare","to wear","regular"],
  ["informare","to inform","regular"],["ingrassare","to gain weight","regular"],
  ["interrompere","to interrupt","irregular"],["intervenire","to intervene","irregular"],
  ["introdurre","to introduce","irregular"],["investire","to invest / to hit","regular"],
  ["lamentarsi","to complain","reflexive"],["lanciare","to launch / to throw","regular"],
  ["laurearsi","to graduate","reflexive"],["legare","to tie","regular"],
  ["lodare","to praise","regular"],["lottare","to struggle","regular"],
  ["lucidare","to polish","regular"],["maledire","to curse","irregular"],
  ["masticare","to chew","regular"],["mescolare","to mix","regular"],
  ["misurare","to measure","regular"],["modificare","to modify","regular"],
  ["montare","to assemble / to whip","regular"],["mordere","to bite","irregular"],
  ["nevicare","to snow","regular"],["nominare","to name / to appoint","regular"],
  ["notare","to notice","regular"],["nutrire","to nourish","regular"],
  ["obbedire","to obey","isco"],["odiare","to hate","regular"],
  ["offendere","to offend","irregular"],["operare","to operate","regular"],
  ["opporre","to oppose","irregular"],["osservare","to observe","regular"],
  ["ottenere","to obtain","irregular"],["parcheggiare","to park","regular"],
  ["pentirsi","to repent","reflexive"],["percorrere","to travel along","irregular"],
  ["perdonare","to forgive","regular"],["permettersi","to afford","reflexive"],
  ["pescare","to fish","regular"],["pettinarsi","to comb one's hair","reflexive"],
  ["piegare","to fold","regular"],["posteggiare","to park","regular"],
  ["pregare","to pray / to beg","regular"],["premere","to press","regular"],
  ["prevedere","to foresee","irregular"],["procedere","to proceed","regular"],
  ["proibire","to forbid","isco"],["pronunciare","to pronounce","regular"],
  ["proteggere","to protect","irregular"],["pubblicare","to publish","regular"],
  ["punire","to punish","isco"],["raccomandare","to recommend","regular"],
  ["raddoppiare","to double","regular"],["raffreddarsi","to catch a cold","reflexive"],
  ["rallentare","to slow down","regular"],["rappresentare","to represent","regular"],
  ["recitare","to act / to recite","regular"],["registrare","to record","regular"],
  ["regnare","to reign","regular"],["respingere","to reject","irregular"],
  ["restaurare","to restore","regular"],["riassumere","to summarize","irregular"],
  ["ribadire","to reaffirm","isco"],["ricevere","to receive","regular"],
  ["richiedere","to require","irregular"],["riempire","to fill","irregular"],
  ["rilassarsi","to relax","reflexive"],["rimandare","to postpone","regular"],
  ["rimproverare","to scold","regular"],["rinunciare","to give up","regular"],
  ["riparare","to repair","regular"],["ripassare","to review","regular"],
  ["riscaldare","to heat","regular"],["rischiare","to risk","regular"],
  ["risparmiare","to save (money)","regular"],["rivolgersi","to turn to","reflexive"],
  ["rompere","to break","irregular"],["rovesciare","to spill / to overturn","regular"],
  ["rovinare","to ruin","regular"],["sanguinare","to bleed","regular"],
  ["sbattere","to slam / to beat","regular"],["sbrigarsi","to hurry up","reflexive"],
  ["scaldare","to warm","regular"],["scambiare","to exchange","regular"],
  ["scaricare","to download / to unload","regular"],["sciare","to ski","regular"],
  ["scivolare","to slip","regular"],["scolpire","to sculpt","isco"],
  ["sconfiggere","to defeat","irregular"],["scontare","to discount","regular"],
  ["scoraggiare","to discourage","regular"],["sdraiarsi","to lie down","reflexive"],
  ["seppellire","to bury","isco"],["servirsi","to help oneself","reflexive"],
  ["sfidare","to challenge","regular"],["sfruttare","to exploit","regular"],
  ["sistemare","to arrange / to sort out","regular"],["soddisfare","to satisfy","irregular"],
  ["sopportare","to bear / to tolerate","regular"],["sorgere","to rise","irregular"],
  ["sorprendere","to surprise","irregular"],["sospettare","to suspect","regular"],
  ["spaventare","to frighten","regular"],["spedire","to send / to ship","isco"],
  ["spogliarsi","to undress","reflexive"],["sporcare","to dirty","regular"],
  ["sposarsi","to get married","reflexive"],["spostarsi","to move (oneself)","reflexive"],
  ["sviluppare","to develop","regular"],["tacere","to be silent","irregular"],
  ["tagliare","to cut","regular"],["tardare","to be late","regular"],
  ["tassare","to tax","regular"],["tentare","to attempt","regular"],
  ["terminare","to finish","regular"],["tossire","to cough","isco"],
  ["tradire","to betray","isco"],["trasferirsi","to relocate","reflexive"],
  ["trasformare","to transform","regular"],["trattare","to treat / to deal with","regular"],
  ["truccarsi","to put on makeup","reflexive"],["ubbidire","to obey","isco"],
  ["unire","to unite / to join","isco"],["urlare","to scream","regular"],
  ["valere","to be worth","irregular"],["versare","to pour","regular"],
  ["vergognarsi","to be ashamed","reflexive"],["vietare","to forbid","regular"],
  ["volgere","to turn","irregular"],["votare","to vote","regular"],
];

// ============================================================
// IRREGULAR FORMS
// ============================================================
// Only the tenses that deviate from the regular pattern are listed; the engine
// merges these over the regular conjugation. Keys: present, imperfect,
// preterite (= passato remoto), future, conditional, cong_presente,
// cong_imperfetto. Non-finite irregularities live in PP_EXACT / GER_EXACT.
const IRREGULAR_FORMS = {
  essere: {
    present:    ["sono","sei","è","siamo","siete","sono"],
    imperfect:  ["ero","eri","era","eravamo","eravate","erano"],
    preterite:  ["fui","fosti","fu","fummo","foste","furono"],
    future:     ["sarò","sarai","sarà","saremo","sarete","saranno"],
    conditional:["sarei","saresti","sarebbe","saremmo","sareste","sarebbero"],
    cong_presente:  ["sia","sia","sia","siamo","siate","siano"],
    cong_imperfetto:["fossi","fossi","fosse","fossimo","foste","fossero"],
  },
  avere: {
    present:    ["ho","hai","ha","abbiamo","avete","hanno"],
    preterite:  ["ebbi","avesti","ebbe","avemmo","aveste","ebbero"],
    future:     ["avrò","avrai","avrà","avremo","avrete","avranno"],
    conditional:["avrei","avresti","avrebbe","avremmo","avreste","avrebbero"],
    cong_presente:  ["abbia","abbia","abbia","abbiamo","abbiate","abbiano"],
  },
  fare: {
    present:    ["faccio","fai","fa","facciamo","fate","fanno"],
    imperfect:  ["facevo","facevi","faceva","facevamo","facevate","facevano"],
    preterite:  ["feci","facesti","fece","facemmo","faceste","fecero"],
    future:     ["farò","farai","farà","faremo","farete","faranno"],
    conditional:["farei","faresti","farebbe","faremmo","fareste","farebbero"],
    cong_presente:  ["faccia","faccia","faccia","facciamo","facciate","facciano"],
  },
  dire: {
    present:    ["dico","dici","dice","diciamo","dite","dicono"],
    imperfect:  ["dicevo","dicevi","diceva","dicevamo","dicevate","dicevano"],
    preterite:  ["dissi","dicesti","disse","dicemmo","diceste","dissero"],
    cong_presente:  ["dica","dica","dica","diciamo","diciate","dicano"],
  },
  potere: {
    present:    ["posso","puoi","può","possiamo","potete","possono"],
    future:     ["potrò","potrai","potrà","potremo","potrete","potranno"],
    conditional:["potrei","potresti","potrebbe","potremmo","potreste","potrebbero"],
    cong_presente:  ["possa","possa","possa","possiamo","possiate","possano"],
  },
  volere: {
    present:    ["voglio","vuoi","vuole","vogliamo","volete","vogliono"],
    preterite:  ["volli","volesti","volle","volemmo","voleste","vollero"],
    future:     ["vorrò","vorrai","vorrà","vorremo","vorrete","vorranno"],
    conditional:["vorrei","vorresti","vorrebbe","vorremmo","vorreste","vorrebbero"],
    cong_presente:  ["voglia","voglia","voglia","vogliamo","vogliate","vogliano"],
  },
  dovere: {
    present:    ["devo","devi","deve","dobbiamo","dovete","devono"],
    future:     ["dovrò","dovrai","dovrà","dovremo","dovrete","dovranno"],
    conditional:["dovrei","dovresti","dovrebbe","dovremmo","dovreste","dovrebbero"],
    cong_presente:  ["debba","debba","debba","dobbiamo","dobbiate","debbano"],
  },
  sapere: {
    present:    ["so","sai","sa","sappiamo","sapete","sanno"],
    preterite:  ["seppi","sapesti","seppe","sapemmo","sapeste","seppero"],
    future:     ["saprò","saprai","saprà","sapremo","saprete","sapranno"],
    conditional:["saprei","sapresti","saprebbe","sapremmo","sapreste","saprebbero"],
    cong_presente:  ["sappia","sappia","sappia","sappiamo","sappiate","sappiano"],
  },
  stare: {
    present:    ["sto","stai","sta","stiamo","state","stanno"],
    preterite:  ["stetti","stesti","stette","stemmo","steste","stettero"],
    future:     ["starò","starai","starà","staremo","starete","staranno"],
    conditional:["starei","staresti","starebbe","staremmo","stareste","starebbero"],
    cong_presente:  ["stia","stia","stia","stiamo","stiate","stiano"],
    cong_imperfetto:["stessi","stessi","stesse","stessimo","steste","stessero"],
  },
  andare: {
    present:    ["vado","vai","va","andiamo","andate","vanno"],
    future:     ["andrò","andrai","andrà","andremo","andrete","andranno"],
    conditional:["andrei","andresti","andrebbe","andremmo","andreste","andrebbero"],
    cong_presente:  ["vada","vada","vada","andiamo","andiate","vadano"],
  },
  dare: {
    present:    ["do","dai","dà","diamo","date","danno"],
    preterite:  ["diedi","desti","diede","demmo","deste","diedero"],
    future:     ["darò","darai","darà","daremo","darete","daranno"],
    conditional:["darei","daresti","darebbe","daremmo","dareste","darebbero"],
    cong_presente:  ["dia","dia","dia","diamo","diate","diano"],
    cong_imperfetto:["dessi","dessi","desse","dessimo","deste","dessero"],
  },
  vedere: {
    preterite:  ["vidi","vedesti","vide","vedemmo","vedeste","videro"],
    future:     ["vedrò","vedrai","vedrà","vedremo","vedrete","vedranno"],
    conditional:["vedrei","vedresti","vedrebbe","vedremmo","vedreste","vedrebbero"],
  },
  venire: {
    present:    ["vengo","vieni","viene","veniamo","venite","vengono"],
    preterite:  ["venni","venisti","venne","venimmo","veniste","vennero"],
    future:     ["verrò","verrai","verrà","verremo","verrete","verranno"],
    conditional:["verrei","verresti","verrebbe","verremmo","verreste","verrebbero"],
    cong_presente:  ["venga","venga","venga","veniamo","veniate","vengano"],
  },
  bere: {
    present:    ["bevo","bevi","beve","beviamo","bevete","bevono"],
    imperfect:  ["bevevo","bevevi","beveva","bevevamo","bevevate","bevevano"],
    preterite:  ["bevvi","bevesti","bevve","bevemmo","beveste","bevvero"],
    future:     ["berrò","berrai","berrà","berremo","berrete","berranno"],
    conditional:["berrei","berresti","berrebbe","berremmo","berreste","berrebbero"],
    cong_presente:  ["beva","beva","beva","beviamo","beviate","bevano"],
  },
  tenere: {
    present:    ["tengo","tieni","tiene","teniamo","tenete","tengono"],
    preterite:  ["tenni","tenesti","tenne","tenemmo","teneste","tennero"],
    future:     ["terrò","terrai","terrà","terremo","terrete","terranno"],
    conditional:["terrei","terresti","terrebbe","terremmo","terreste","terrebbero"],
    cong_presente:  ["tenga","tenga","tenga","teniamo","teniate","tengano"],
  },
  rimanere: {
    present:    ["rimango","rimani","rimane","rimaniamo","rimanete","rimangono"],
    preterite:  ["rimasi","rimanesti","rimase","rimanemmo","rimaneste","rimasero"],
    future:     ["rimarrò","rimarrai","rimarrà","rimarremo","rimarrete","rimarranno"],
    conditional:["rimarrei","rimarresti","rimarrebbe","rimarremmo","rimarreste","rimarrebbero"],
    cong_presente:  ["rimanga","rimanga","rimanga","rimaniamo","rimaniate","rimangano"],
  },
  uscire: {
    present:    ["esco","esci","esce","usciamo","uscite","escono"],
    cong_presente:  ["esca","esca","esca","usciamo","usciate","escano"],
  },
  riuscire: {
    present:    ["riesco","riesci","riesce","riusciamo","riuscite","riescono"],
    cong_presente:  ["riesca","riesca","riesca","riusciamo","riusciate","riescano"],
  },
  parere: {
    present:    ["paio","pari","pare","paiamo","parete","paiono"],
    preterite:  ["parvi","paresti","parve","paremmo","pareste","parvero"],
    future:     ["parrò","parrai","parrà","parremo","parrete","parranno"],
    conditional:["parrei","parresti","parrebbe","parremmo","parreste","parrebbero"],
    cong_presente:  ["paia","paia","paia","paiamo","paiate","paiano"],
  },
  sedere: {
    present:    ["siedo","siedi","siede","sediamo","sedete","siedono"],
    cong_presente:  ["sieda","sieda","sieda","sediamo","sediate","siedano"],
  },
  salire: {
    present:    ["salgo","sali","sale","saliamo","salite","salgono"],
    cong_presente:  ["salga","salga","salga","saliamo","saliate","salgano"],
  },
  morire: {
    present:    ["muoio","muori","muore","moriamo","morite","muoiono"],
    future:     ["morirò","morirai","morirà","moriremo","morirete","moriranno"],
    conditional:["morirei","moriresti","morirebbe","moriremmo","morireste","morirebbero"],
    cong_presente:  ["muoia","muoia","muoia","moriamo","moriate","muoiano"],
  },
  piacere: {
    present:    ["piaccio","piaci","piace","piacciamo","piacete","piacciono"],
    preterite:  ["piacqui","piacesti","piacque","piacemmo","piaceste","piacquero"],
    cong_presente:  ["piaccia","piaccia","piaccia","piacciamo","piacciate","piacciano"],
  },
  scegliere: {
    present:    ["scelgo","scegli","sceglie","scegliamo","scegliete","scelgono"],
    preterite:  ["scelsi","scegliesti","scelse","scegliemmo","sceglieste","scelsero"],
    cong_presente:  ["scelga","scelga","scelga","scegliamo","scegliate","scelgano"],
  },
  togliere: {
    present:    ["tolgo","togli","toglie","togliamo","togliete","tolgono"],
    preterite:  ["tolsi","togliesti","tolse","togliemmo","toglieste","tolsero"],
    cong_presente:  ["tolga","tolga","tolga","togliamo","togliate","tolgano"],
  },
  raccogliere: {
    present:    ["raccolgo","raccogli","raccoglie","raccogliamo","raccogliete","raccolgono"],
    preterite:  ["raccolsi","raccogliesti","raccolse","raccogliemmo","raccoglieste","raccolsero"],
    cong_presente:  ["raccolga","raccolga","raccolga","raccogliamo","raccogliate","raccolgano"],
  },
  // -ere verbs irregular only in passato remoto (1-3-3) + participio:
  prendere:  { preterite: ["presi","prendesti","prese","prendemmo","prendeste","presero"] },
  mettere:   { preterite: ["misi","mettesti","mise","mettemmo","metteste","misero"] },
  scrivere:  { preterite: ["scrissi","scrivesti","scrisse","scrivemmo","scriveste","scrissero"] },
  leggere:   { preterite: ["lessi","leggesti","lesse","leggemmo","leggeste","lessero"] },
  chiedere:  { preterite: ["chiesi","chiedesti","chiese","chiedemmo","chiedeste","chiesero"] },
  chiudere:  { preterite: ["chiusi","chiudesti","chiuse","chiudemmo","chiudeste","chiusero"] },
  conoscere: { preterite: ["conobbi","conoscesti","conobbe","conoscemmo","conosceste","conobbero"] },
  nascere:   { preterite: ["nacqui","nascesti","nacque","nascemmo","nasceste","nacquero"] },
  correre:   { preterite: ["corsi","corresti","corse","corremmo","correste","corsero"] },
  perdere:   { preterite: ["persi","perdesti","perse","perdemmo","perdeste","persero"] },
  decidere:  { preterite: ["decisi","decidesti","decise","decidemmo","decideste","decisero"] },
  ridere:    { preterite: ["risi","ridesti","rise","ridemmo","rideste","risero"] },
  rispondere:{ preterite: ["risposi","rispondesti","rispose","rispondemmo","rispondeste","risposero"] },
  vincere:   { preterite: ["vinsi","vincesti","vinse","vincemmo","vinceste","vinsero"] },
  spendere:  { preterite: ["spesi","spendesti","spese","spendemmo","spendeste","spesero"] },
  spingere:  { preterite: ["spinsi","spingesti","spinse","spingemmo","spingeste","spinsero"] },
  piangere:  { preterite: ["piansi","piangesti","pianse","piangemmo","piangeste","piansero"] },
  dipingere: { preterite: ["dipinsi","dipingesti","dipinse","dipingemmo","dipingeste","dipinsero"] },
  rendere:   { preterite: ["resi","rendesti","rese","rendemmo","rendeste","resero"] },
  nascondere:{ preterite: ["nascosi","nascondesti","nascose","nascondemmo","nascondeste","nascosero"] },
  vivere: {
    preterite:  ["vissi","vivesti","visse","vivemmo","viveste","vissero"],
    future:     ["vivrò","vivrai","vivrà","vivremo","vivrete","vivranno"],
    conditional:["vivrei","vivresti","vivrebbe","vivremmo","vivreste","vivrebbero"],
  },
  cadere: {
    preterite:  ["caddi","cadesti","cadde","cademmo","cadeste","caddero"],
    future:     ["cadrò","cadrai","cadrà","cadremo","cadrete","cadranno"],
    conditional:["cadrei","cadresti","cadrebbe","cadremmo","cadreste","cadrebbero"],
  },
  muovere: {
    preterite:  ["mossi","movesti","mosse","movemmo","moveste","mossero"],
  },
  // -urre verbs (latin -ducere): stem -duc-, future -urrò.
  tradurre: {
    present:    ["traduco","traduci","traduce","traduciamo","traducete","traducono"],
    imperfect:  ["traducevo","traducevi","traduceva","traducevamo","traducevate","traducevano"],
    preterite:  ["tradussi","traducesti","tradusse","traducemmo","traduceste","tradussero"],
    future:     ["tradurrò","tradurrai","tradurrà","tradurremo","tradurrete","tradurranno"],
    cong_presente:  ["traduca","traduca","traduca","traduciamo","traduciate","traducano"],
  },
  produrre: {
    present:    ["produco","produci","produce","produciamo","producete","producono"],
    imperfect:  ["producevo","producevi","produceva","producevamo","producevate","producevano"],
    preterite:  ["produssi","producesti","produsse","producemmo","produceste","produssero"],
    future:     ["produrrò","produrrai","produrrà","produrremo","produrrete","produrranno"],
    cong_presente:  ["produca","produca","produca","produciamo","produciate","producano"],
  },
  ridurre: {
    present:    ["riduco","riduci","riduce","riduciamo","riducete","riducono"],
    imperfect:  ["riducevo","riducevi","riduceva","riducevamo","riducevate","riducevano"],
    preterite:  ["ridussi","riducesti","ridusse","riducemmo","riduceste","ridussero"],
    future:     ["ridurrò","ridurrai","ridurrà","ridurremo","ridurrete","ridurranno"],
    cong_presente:  ["riduca","riduca","riduca","riduciamo","riduciate","riducano"],
  },
  // -orre verbs (latin -ponere): stem -pon-, future -orrò.
  porre: {
    present:    ["pongo","poni","pone","poniamo","ponete","pongono"],
    imperfect:  ["ponevo","ponevi","poneva","ponevamo","ponevate","ponevano"],
    preterite:  ["posi","ponesti","pose","ponemmo","poneste","posero"],
    future:     ["porrò","porrai","porrà","porremo","porrete","porranno"],
    cong_presente:  ["ponga","ponga","ponga","poniamo","poniate","pongano"],
  },
  proporre: {
    present:    ["propongo","proponi","propone","proponiamo","proponete","propongono"],
    imperfect:  ["proponevo","proponevi","proponeva","proponevamo","proponevate","proponevano"],
    preterite:  ["proposi","proponesti","propose","proponemmo","proponeste","proposero"],
    future:     ["proporrò","proporrai","proporrà","proporremo","proporrete","proporranno"],
    cong_presente:  ["proponga","proponga","proponga","proponiamo","proponiate","propongano"],
  },
  // --- expansion-batch irregulars (tenere/venire/porre/urre/dire families + standalone) ---
  ottenere: {
    present:["ottengo","ottieni","ottiene","otteniamo","ottenete","ottengono"],
    preterite:["ottenni","ottenesti","ottenne","ottenemmo","otteneste","ottennero"],
    future:["otterrò","otterrai","otterrà","otterremo","otterrete","otterranno"],
    cong_presente:["ottenga","ottenga","ottenga","otteniamo","otteniate","ottengano"],
  },
  mantenere: {
    present:["mantengo","mantieni","mantiene","manteniamo","mantenete","mantengono"],
    preterite:["mantenni","mantenesti","mantenne","mantenemmo","manteneste","mantennero"],
    future:["manterrò","manterrai","manterrà","manterremo","manterrete","manterranno"],
    cong_presente:["mantenga","mantenga","mantenga","manteniamo","manteniate","mantengano"],
  },
  sostenere: {
    present:["sostengo","sostieni","sostiene","sosteniamo","sostenete","sostengono"],
    preterite:["sostenni","sostenesti","sostenne","sostenemmo","sosteneste","sostennero"],
    future:["sosterrò","sosterrai","sosterrà","sosterremo","sosterrete","sosterranno"],
    cong_presente:["sostenga","sostenga","sostenga","sosteniamo","sosteniate","sostengano"],
  },
  intervenire: {
    present:["intervengo","intervieni","interviene","interveniamo","intervenite","intervengono"],
    preterite:["intervenni","intervenisti","intervenne","intervenimmo","interveniste","intervennero"],
    future:["interverrò","interverrai","interverrà","interverremo","interverrete","interverranno"],
    cong_presente:["intervenga","intervenga","intervenga","interveniamo","interveniate","intervengano"],
  },
  avvenire: {
    present:["avvengo","avvieni","avviene","avveniamo","avvenite","avvengono"],
    preterite:["avvenni","avvenisti","avvenne","avvenimmo","avveniste","avvennero"],
    future:["avverrò","avverrai","avverrà","avverremo","avverrete","avverranno"],
    cong_presente:["avvenga","avvenga","avvenga","avveniamo","avveniate","avvengano"],
  },
  opporre: {
    present:["oppongo","opponi","oppone","opponiamo","opponete","oppongono"],
    imperfect:["opponevo","opponevi","opponeva","opponevamo","opponevate","opponevano"],
    preterite:["opposi","opponesti","oppose","opponemmo","opponeste","opposero"],
    future:["opporrò","opporrai","opporrà","opporremo","opporrete","opporranno"],
    cong_presente:["opponga","opponga","opponga","opponiamo","opponiate","oppongano"],
  },
  comporre: {
    present:["compongo","componi","compone","componiamo","componete","compongono"],
    imperfect:["componevo","componevi","componeva","componevamo","componevate","componevano"],
    preterite:["composi","componesti","compose","componemmo","componeste","composero"],
    future:["comporrò","comporrai","comporrà","comporremo","comporrete","comporranno"],
    cong_presente:["componga","componga","componga","componiamo","componiate","compongano"],
  },
  introdurre: {
    present:["introduco","introduci","introduce","introduciamo","introducete","introducono"],
    imperfect:["introducevo","introducevi","introduceva","introducevamo","introducevate","introducevano"],
    preterite:["introdussi","introducesti","introdusse","introducemmo","introduceste","introdussero"],
    future:["introdurrò","introdurrai","introdurrà","introdurremo","introdurrete","introdurranno"],
    cong_presente:["introduca","introduca","introduca","introduciamo","introduciate","introducano"],
  },
  maledire: {
    present:["maledico","maledici","maledice","malediciamo","maledite","maledicono"],
    imperfect:["maledicevo","maledicevi","malediceva","maledicevamo","maledicevate","maledicevano"],
    preterite:["maledissi","maledicesti","maledisse","maledicemmo","malediceste","maledissero"],
    cong_presente:["maledica","maledica","maledica","malediciamo","maledicate","maledicano"],
  },
  apparire: {
    present:["appaio","appari","appare","appariamo","apparite","appaiono"],
    preterite:["apparvi","apparisti","apparve","apparimmo","appariste","apparvero"],
    cong_presente:["appaia","appaia","appaia","appariamo","appariate","appaiano"],
  },
  tacere: {
    present:["taccio","taci","tace","tacciamo","tacete","tacciono"],
    preterite:["tacqui","tacesti","tacque","tacemmo","taceste","tacquero"],
    cong_presente:["taccia","taccia","taccia","tacciamo","tacciate","tacciano"],
  },
  valere: {
    present:["valgo","vali","vale","valiamo","valete","valgono"],
    preterite:["valsi","valesti","valse","valemmo","valeste","valsero"],
    future:["varrò","varrai","varrà","varremo","varrete","varranno"],
    cong_presente:["valga","valga","valga","valiamo","valiate","valgano"],
  },
  accadere: {
    present:["accado","accadi","accade","accadiamo","accadete","accadono"],
    preterite:["accaddi","accadesti","accadde","accademmo","accadeste","accaddero"],
    future:["accadrò","accadrai","accadrà","accadremo","accadrete","accadranno"],
  },
  // -ere verbs irregular in passato remoto only (1-3-3); participio is in PP_EXACT
  rompere:      { preterite:["ruppi","rompesti","ruppe","rompemmo","rompeste","ruppero"] },
  assumere:     { preterite:["assunsi","assumesti","assunse","assumemmo","assumeste","assunsero"] },
  riassumere:   { preterite:["riassunsi","riassumesti","riassunse","riassumemmo","riassumeste","riassunsero"] },
  concedere:    { preterite:["concessi","concedesti","concesse","concedemmo","concedeste","concessero"] },
  condividere:  { preterite:["condivisi","condividesti","condivise","condividemmo","condivideste","condivisero"] },
  confondere:   { preterite:["confusi","confondesti","confuse","confondemmo","confondeste","confusero"] },
  convincere:   { preterite:["convinsi","convincesti","convinse","convincemmo","convinceste","convinsero"] },
  correggere:   { preterite:["corressi","correggesti","corresse","correggemmo","correggeste","corressero"] },
  corrispondere:{ preterite:["corrisposi","corrispondesti","corrispose","corrispondemmo","corrispondeste","corrisposero"] },
  dipendere:    { preterite:["dipesi","dipendesti","dipese","dipendemmo","dipendeste","dipesero"] },
  escludere:    { preterite:["esclusi","escludesti","escluse","escludemmo","escludeste","esclusero"] },
  concludere:   { preterite:["conclusi","concludesti","concluse","concludemmo","concludeste","conclusero"] },
  deludere:     { preterite:["delusi","deludesti","deluse","deludemmo","deludeste","delusero"] },
  evadere:      { preterite:["evasi","evadesti","evase","evademmo","evadeste","evasero"] },
  friggere:     { preterite:["frissi","friggesti","frisse","friggemmo","friggeste","frissero"] },
  interrompere: { preterite:["interruppi","interrompesti","interruppe","interrompemmo","interrompeste","interruppero"] },
  mordere:      { preterite:["morsi","mordesti","morse","mordemmo","mordeste","morsero"] },
  offendere:    { preterite:["offesi","offendesti","offese","offendemmo","offendeste","offesero"] },
  proteggere:   { preterite:["protessi","proteggesti","protesse","proteggemmo","proteggeste","protessero"] },
  sconfiggere:  { preterite:["sconfissi","sconfiggesti","sconfisse","sconfiggemmo","sconfiggeste","sconfissero"] },
  sorgere:      { preterite:["sorsi","sorgesti","sorse","sorgemmo","sorgeste","sorsero"] },
  volgere:      { preterite:["volsi","volgesti","volse","volgemmo","volgeste","volsero"] },
  rivolgere:    { preterite:["rivolsi","rivolgesti","rivolse","rivolgemmo","rivolgeste","rivolsero"] },
  accorgere:    { preterite:["accorsi","accorgesti","accorse","accorgemmo","accorgeste","accorsero"] },
  percorrere:   { preterite:["percorsi","percorresti","percorse","percorremmo","percorreste","percorsero"] },
  richiedere:   { preterite:["richiesi","richiedesti","richiese","richiedemmo","richiedeste","richiesero"] },
  sorprendere:  { preterite:["sorpresi","sorprendesti","sorprese","sorprendemmo","sorprendeste","sorpresero"] },
  raggiungere:  { preterite:["raggiunsi","raggiungesti","raggiunse","raggiungemmo","raggiungeste","raggiunsero"] },
  aggiungere:   { preterite:["aggiunsi","aggiungesti","aggiunse","aggiungemmo","aggiungeste","aggiunsero"] },
  giungere:     { preterite:["giunsi","giungesti","giunse","giungemmo","giungeste","giunsero"] },
  prevedere: {
    preterite:["previdi","prevedesti","previde","prevedemmo","prevedeste","previdero"],
    future:["prevedrò","prevedrai","prevedrà","prevedremo","prevedrete","prevedranno"],
  },
  // passato-remoto (1-3-3) coverage for remaining irregular -ere verbs
  accendere:   { preterite:["accesi","accendesti","accese","accendemmo","accendeste","accesero"] },
  comprendere: { preterite:["compresi","comprendesti","comprese","comprendemmo","comprendeste","compresero"] },
  crescere:    { preterite:["crebbi","crescesti","crebbe","crescemmo","cresceste","crebbero"] },
  descrivere:  { preterite:["descrissi","descrivesti","descrisse","descrivemmo","descriveste","descrissero"] },
  dirigere:    { preterite:["diressi","dirigesti","diresse","dirigemmo","dirigeste","diressero"] },
  discutere:   { preterite:["discussi","discutesti","discusse","discutemmo","discuteste","discussero"] },
  dividere:    { preterite:["divisi","dividesti","divise","dividemmo","divideste","divisero"] },
  esprimere:   { preterite:["espressi","esprimesti","espresse","esprimemmo","esprimeste","espressero"] },
  fingere:     { preterite:["finsi","fingesti","finse","fingemmo","fingeste","finsero"] },
  permettere:  { preterite:["permisi","permettesti","permise","permettemmo","permetteste","permisero"] },
  promettere:  { preterite:["promisi","promettesti","promise","promettemmo","prometteste","promisero"] },
  riconoscere: { preterite:["riconobbi","riconoscesti","riconobbe","riconoscemmo","riconosceste","riconobbero"] },
  risolvere:   { preterite:["risolsi","risolvesti","risolse","risolvemmo","risolveste","risolsero"] },
  scendere:    { preterite:["scesi","scendesti","scese","scendemmo","scendeste","scesero"] },
  smettere:    { preterite:["smisi","smettesti","smise","smettemmo","smetteste","smisero"] },
  sorridere:   { preterite:["sorrisi","sorridesti","sorrise","sorridemmo","sorrideste","sorrisero"] },
  stringere:   { preterite:["strinsi","stringesti","strinse","stringemmo","stringeste","strinsero"] },
  succedere:   { preterite:["successi","succedesti","successe","succedemmo","succedeste","successero"] },
  svolgere:    { preterite:["svolsi","svolgesti","svolse","svolgemmo","svolgeste","svolsero"] },
  trascorrere: { preterite:["trascorsi","trascorresti","trascorse","trascorremmo","trascorreste","trascorsero"] },
  respingere:  { preterite:["respinsi","respingesti","respinse","respingemmo","respingeste","respinsero"] },
  // verbs with an irregular present too
  possedere: {
    present:["possiedo","possiedi","possiede","possediamo","possedete","possiedono"],
    preterite:["possedetti","possedesti","possedette","possedemmo","possedeste","possedettero"],
    cong_presente:["possieda","possieda","possieda","possediamo","possediate","possiedano"],
  },
  spegnere: {
    present:["spengo","spegni","spegne","spegniamo","spegnete","spengono"],
    preterite:["spensi","spegnesti","spense","spegnemmo","spegneste","spensero"],
    cong_presente:["spenga","spenga","spenga","spegniamo","spegniate","spengano"],
  },
  dispiacere: {
    present:["dispiaccio","dispiaci","dispiace","dispiacciamo","dispiacete","dispiacciono"],
    preterite:["dispiacqui","dispiacesti","dispiacque","dispiacemmo","dispiaceste","dispiacquero"],
    cong_presente:["dispiaccia","dispiaccia","dispiaccia","dispiacciamo","dispiacciate","dispiacciano"],
  },
  riempire: {
    present:["riempio","riempi","riempie","riempiamo","riempite","riempiono"],
    cong_presente:["riempia","riempia","riempia","riempiamo","riempiate","riempiano"],
  },
  soddisfare: {
    present:["soddisfo","soddisfi","soddisfa","soddisfiamo","soddisfate","soddisfano"],
    preterite:["soddisfeci","soddisfacesti","soddisfece","soddisfacemmo","soddisfaceste","soddisfecero"],
    future:["soddisfarò","soddisfarai","soddisfarà","soddisfaremo","soddisfarete","soddisfaranno"],
    cong_presente:["soddisfi","soddisfi","soddisfi","soddisfiamo","soddisfiate","soddisfino"],
  },
};

// -isco ("inchoative") -ire verbs: present + congiuntivo presente insert -isc-.
// Populated from the verb lists (group "isco") by buildVerbList().
const ISCO_VERBS = new Set();

// Verbs whose compound tenses take ESSERE (the rest take avere). Reflexives
// (infinitive ends in -rsi) always take essere and are detected separately.
const ESSERE_AUX = new Set([
  "essere","stare","andare","venire","arrivare","partire","tornare","ritornare",
  "entrare","uscire","restare","rimanere","diventare","divenire","nascere","morire",
  "cadere","salire","scendere","piacere","dispiacere","riuscire","sembrare","parere",
  "succedere","accadere","capitare","bastare","costare","durare","esistere","scappare",
  "fuggire","giungere","crescere","vivere",
]);

// ============================================================
// VOCABULARY (small inline starter set used by a few legacy views;
// the main themed vocabulary lives in vocabulary.js + vocab-*.js)
// ============================================================
const VOCABULARY = [
  { es: "casa", en: "house", cat: "noun" },{ es: "cane", en: "dog", cat: "noun" },
  { es: "gatto", en: "cat", cat: "noun" },{ es: "acqua", en: "water", cat: "noun" },
  { es: "cibo", en: "food", cat: "noun" },{ es: "libro", en: "book", cat: "noun" },
  { es: "amico", en: "friend", cat: "noun" },{ es: "famiglia", en: "family", cat: "noun" },
  { es: "tempo", en: "time / weather", cat: "noun" },{ es: "lavoro", en: "work / job", cat: "noun" },
  { es: "città", en: "city", cat: "noun" },{ es: "paese", en: "country / town", cat: "noun" },
  { es: "mondo", en: "world", cat: "noun" },{ es: "vita", en: "life", cat: "noun" },
  { es: "giorno", en: "day", cat: "noun" },{ es: "notte", en: "night", cat: "noun" },
  { es: "anno", en: "year", cat: "noun" },{ es: "mese", en: "month", cat: "noun" },
  { es: "settimana", en: "week", cat: "noun" },{ es: "ora", en: "hour", cat: "noun" },
  { es: "grande", en: "big", cat: "adj" },{ es: "piccolo", en: "small", cat: "adj" },
  { es: "buono", en: "good", cat: "adj" },{ es: "cattivo", en: "bad", cat: "adj" },
  { es: "nuovo", en: "new", cat: "adj" },{ es: "vecchio", en: "old", cat: "adj" },
  { es: "felice", en: "happy", cat: "adj" },{ es: "triste", en: "sad", cat: "adj" },
  { es: "bello", en: "beautiful", cat: "adj" },{ es: "brutto", en: "ugly", cat: "adj" },
  { es: "veloce", en: "fast", cat: "adj" },{ es: "lento", en: "slow", cat: "adj" },
  { es: "alto", en: "tall / high", cat: "adj" },{ es: "basso", en: "short / low", cat: "adj" },
  { es: "caro", en: "expensive / dear", cat: "adj" },{ es: "economico", en: "cheap", cat: "adj" },
  { es: "rosso", en: "red", cat: "color" },{ es: "azzurro", en: "(sky) blue", cat: "color" },
  { es: "verde", en: "green", cat: "color" },{ es: "giallo", en: "yellow", cat: "color" },
  { es: "nero", en: "black", cat: "color" },{ es: "bianco", en: "white", cat: "color" },
  { es: "ciao", en: "hi / bye", cat: "phrase" },{ es: "arrivederci", en: "goodbye", cat: "phrase" },
  { es: "per favore", en: "please", cat: "phrase" },{ es: "grazie", en: "thank you", cat: "phrase" },
  { es: "prego", en: "you're welcome", cat: "phrase" },{ es: "mi dispiace", en: "I'm sorry", cat: "phrase" },
  { es: "buongiorno", en: "good morning", cat: "phrase" },{ es: "buonanotte", en: "good night", cat: "phrase" },
];

// Visual dual-coding for concrete, picturable verbs. Keyed by infinitive.
const VERB_EMOJI = {
  abbracciare: "🤗", aprire: "🔓", ballare: "💃", bere: "🥤", baciare: "😘",
  camminare: "🚶", cantare: "🎤", chiudere: "🔒", cucinare: "🍳", mangiare: "🍽️",
  comprare: "🛒", guidare: "🚗", costruire: "🏗️", correre: "🏃", tagliare: "✂️",
  riposare: "😌", svegliarsi: "⏰", disegnare: "✏️", dormire: "😴", inviare: "📨",
  nascondere: "🙈", ascoltare: "👂", studiare: "📚", firmare: "🖊️", fumare: "🚬",
  vincere: "🏆", gridare: "📢", giocare: "🎮", lavare: "🧼", leggere: "📖",
  pulire: "🧹", piangere: "😢", piovere: "🌧️", nuotare: "🏊", pagare: "💳",
  pescare: "🎣", dipingere: "🎨", respirare: "🫁", ridere: "😄", rompere: "💥",
  saltare: "🤸", sorridere: "🙂", suonare: "🎸", viaggiare: "✈️", volare: "🛫",
  scrivere: "✍️", telefonare: "📞", sognare: "💤", cadere: "🍂",
};

// Build the complete verb list (top + additional, deduplicated) and populate
// the ISCO set from the group metadata.
function buildVerbList() {
  const seen = new Set();
  const verbs = [];
  TOP_50_VERBS.forEach((v, i) => {
    seen.add(v.inf);
    if (v.group === "isco") ISCO_VERBS.add(v.inf);
    verbs.push({ ...v, emoji: v.emoji || VERB_EMOJI[v.inf] || "", rank: i + 1, top: true });
  });
  ADDITIONAL_VERBS.forEach(([inf, en, group], i) => {
    if (seen.has(inf)) return;
    seen.add(inf);
    if (group === "isco") ISCO_VERBS.add(inf);
    verbs.push({ inf, en, group, rank: TOP_50_VERBS.length + i + 1, top: false, emoji: VERB_EMOJI[inf] || "", note: "" });
  });
  return verbs;
}

const ALL_VERBS = buildVerbList();

// ============================================================
// TENSE METADATA
// ============================================================
const PRONOUNS = ["io", "tu", "lui/lei", "noi", "voi", "loro"];
// Keys are kept identical to the original app so the ~50 views need no churn;
// "preterite" is Italian's passato remoto.
const TENSES = ["present", "preterite", "imperfect", "conditional", "future"];
const TENSE_LABELS = {
  present: "Presente",
  preterite: "Passato Remoto",
  imperfect: "Imperfetto",
  conditional: "Condizionale Presente",
  future: "Futuro Semplice",
};

// ============================================================
// REGULAR CONJUGATION
// ============================================================
function stripReflexive(infinitive) {
  // lavarsi → lavare, mettersi → mettere, vestirsi → vestire
  if (/rsi$/.test(infinitive) && infinitive.length > 3) {
    return infinitive.slice(0, -2) + "e";
  }
  return infinitive;
}

function getStemAndType(infinitive) {
  const inf = stripReflexive(infinitive);
  const last3 = inf.slice(-3);
  if (last3 === "are") return { stem: inf.slice(0, -3), type: "are", base: inf };
  if (last3 === "ere") return { stem: inf.slice(0, -3), type: "ere", base: inf };
  if (last3 === "ire") {
    const type = ISCO_VERBS.has(inf) ? "ireIsco" : "ire";
    return { stem: inf.slice(0, -3), type, base: inf };
  }
  // -urre (tradurre) / -orre (porre) / -arre (trarre): handled via irregulars;
  // fall back to a best-effort -ere-like stem.
  return { stem: inf.slice(0, -1), type: "ere", base: inf };
}

const REGULAR_ENDINGS = {
  present: {
    are:     ["o","i","a","iamo","ate","ano"],
    ere:     ["o","i","e","iamo","ete","ono"],
    ire:     ["o","i","e","iamo","ite","ono"],
    ireIsco: ["isco","isci","isce","iamo","ite","iscono"],
  },
  // passato remoto
  preterite: {
    are: ["ai","asti","ò","ammo","aste","arono"],
    ere: ["ei","esti","é","emmo","este","erono"],
    ire: ["ii","isti","ì","immo","iste","irono"],
  },
  imperfect: {
    are: ["avo","avi","ava","avamo","avate","avano"],
    ere: ["evo","evi","eva","evamo","evate","evano"],
    ire: ["ivo","ivi","iva","ivamo","ivate","ivano"],
  },
  // endings already carry the theme vowel, so they attach to the bare stem
  conditional: {
    are: ["erei","eresti","erebbe","eremmo","ereste","erebbero"],
    ere: ["erei","eresti","erebbe","eremmo","ereste","erebbero"],
    ire: ["irei","iresti","irebbe","iremmo","ireste","irebbero"],
  },
  future: {
    are: ["erò","erai","erà","eremo","erete","eranno"],
    ere: ["erò","erai","erà","eremo","erete","eranno"],
    ire: ["irò","irai","irà","iremo","irete","iranno"],
  },
};

// Orthographic join: keep the hard/soft consonant sound when stem meets ending.
//   -care/-gare  → insert "h" before e/i      (cerco → cerchi, cercherò)
//   -ciare/-giare/-sciare → drop stem's final i before e/i (mangio → mangerò)
//   -iare (plain) → collapse a doubled i        (studio → studi, not studii)
function joinStem(base, stem, ending) {
  const startsEI = /^[eiéìí]/.test(ending);
  if (/c$/.test(stem) && /care$/.test(base) && startsEI) return stem + "h" + ending;
  if (/g$/.test(stem) && /gare$/.test(base) && startsEI) return stem + "h" + ending;
  if (/(ci|gi|sci)$/.test(stem) && /(ciare|giare|sciare)$/.test(base) && startsEI) {
    return stem.slice(0, -1) + ending;            // drop the soft-marker i
  }
  if (/iare$/.test(base) && /^i/.test(ending)) {
    return stem.slice(0, -1) + ending;            // studi-are: avoid studii
  }
  return stem + ending;
}

function regularConjugate(infinitive, tense) {
  const { stem, type, base } = getStemAndType(infinitive);
  const table = REGULAR_ENDINGS[tense];
  // -isco verbs only differ in the present; every other tense uses plain -ire.
  const key = type === "ireIsco" ? (tense === "present" ? "ireIsco" : "ire") : type;
  const endings = table[key] || table.ere;
  return endings.map((e) => joinStem(base, stem, e));
}

function conjugate(infinitive, tense) {
  const base = stripReflexive(infinitive);
  const irregular = IRREGULAR_FORMS[base];
  const regular = regularConjugate(infinitive, tense);
  if (irregular && irregular[tense]) {
    return { forms: irregular[tense], regular };
  }
  // Conditional shares the (possibly irregular) future stem.
  if (tense === "conditional" && irregular && irregular.future) {
    const stem = irregular.future[0].slice(0, -1); // "andrò" → "andr"
    const endings = ["ei","esti","ebbe","emmo","este","ebbero"];
    return { forms: endings.map((e) => stem + e), regular };
  }
  return { forms: regular, regular };
}

function getIrregularFlags(infinitive, tense) {
  const { forms, regular } = conjugate(infinitive, tense);
  return forms.map((f, i) => f !== regular[i]);
}

// ============================================================
// CONGIUNTIVO (SUBJUNCTIVE)
// ============================================================
const SUBJUNCTIVE_TENSES = ["cong_presente", "cong_imperfetto"];
const SUBJUNCTIVE_LABELS = {
  cong_presente: "Congiuntivo Presente",
  cong_imperfetto: "Congiuntivo Imperfetto",
};

const CONG_PRES_ENDINGS = {
  are:     ["i","i","i","iamo","iate","ino"],
  ere:     ["a","a","a","iamo","iate","ano"],
  ire:     ["a","a","a","iamo","iate","ano"],
  ireIsco: ["isca","isca","isca","iamo","iate","iscano"],
};

function regularCongPresente(infinitive) {
  const { stem, type, base } = getStemAndType(infinitive);
  const endings = CONG_PRES_ENDINGS[type] || CONG_PRES_ENDINGS.ere;
  return endings.map((e) => joinStem(base, stem, e));
}

// Congiuntivo imperfetto: take the imperfetto io-form, drop "-vo", add
// -ssi/-ssi/-sse/-ssimo/-ste/-ssero. Falls back to the regular -assi/-essi/-issi
// when the stem can't be derived. Irregular overrides (essere, dare, stare) are
// stored explicitly in IRREGULAR_FORMS.cong_imperfetto.
const CONG_IMPF_TAILS = ["ssi","ssi","sse","ssimo","ste","ssero"];
function regularCongImperfetto(infinitive) {
  const base = stripReflexive(infinitive);
  const irr = IRREGULAR_FORMS[base];
  const impfIo = (irr && irr.imperfect ? irr.imperfect[0] : regularConjugate(base, "imperfect")[0]);
  if (impfIo.endsWith("vo")) {
    const root = impfIo.slice(0, -2); // "parlavo" → "parla", "facevo" → "face"
    return CONG_IMPF_TAILS.map((t) => root + t);
  }
  const { stem, type } = getStemAndType(base);
  const vowel = type === "are" ? "a" : type.startsWith("ire") ? "i" : "e";
  return CONG_IMPF_TAILS.map((t) => stem + vowel + t);
}

function conjugateSubj(infinitive, tense) {
  const base = stripReflexive(infinitive);
  const irr = IRREGULAR_FORMS[base];
  if (tense === "cong_presente") {
    const regular = regularCongPresente(base);
    if (irr && irr.cong_presente) return { forms: irr.cong_presente, regular };
    return { forms: regular, regular };
  }
  // cong_imperfetto
  const regular = regularCongImperfetto(base);
  if (irr && irr.cong_imperfetto) return { forms: irr.cong_imperfetto, regular };
  return { forms: regular, regular };
}

function presentSubj(infinitive) { return conjugateSubj(infinitive, "cong_presente"); }

// ============================================================
// IMPERATIVO
// ============================================================
// tu (informal), Lei (formal sg), noi, voi, Loro (formal pl, rare).
const IMPERATIVE_ROWS = ["tu", "Lei", "noi", "voi", "Loro"];
const IMPERATIVE_LABELS = {
  tu: "tu", Lei: "Lei", noi: "noi", voi: "voi", Loro: "Loro",
};
// Irregular affirmative tu forms.
const TU_AFF_IRREGULAR = {
  essere: "sii", avere: "abbi", andare: "va'", dare: "da'",
  fare: "fa'", dire: "di'", stare: "sta'",
};
// Irregular affirmative voi forms (take the congiuntivo, not the indicative).
const VOI_AFF_IRREGULAR = {
  essere: "siate", avere: "abbiate", sapere: "sappiate", volere: "vogliate",
};

function imperative(infinitive) {
  const base = stripReflexive(infinitive);
  const { type } = getStemAndType(base);
  const { forms: pres, regular: presReg } = conjugate(base, "present");
  const { forms: subj, regular: subjReg } = presentSubj(base);

  // tu: -are uses the present 3rd-singular (parla); -ere/-ire use 2nd-singular.
  const tuIdx = type === "are" ? 2 : 1;
  let tuAff = pres[tuIdx];
  const tuAffReg = presReg[tuIdx];
  if (TU_AFF_IRREGULAR[base]) tuAff = TU_AFF_IRREGULAR[base];

  let voiAff = pres[4];
  if (VOI_AFF_IRREGULAR[base]) voiAff = VOI_AFF_IRREGULAR[base];

  // Negative tu is "non" + the infinitive (non parlare); others "non" + form.
  const neg = (s) => "non " + s;
  return {
    tu:   { aff: tuAff,   neg: neg(base),    affReg: tuAffReg,  negReg: neg(base) },
    Lei:  { aff: subj[2], neg: neg(subj[2]), affReg: subjReg[2], negReg: neg(subjReg[2]) },
    noi:  { aff: pres[3], neg: neg(pres[3]), affReg: presReg[3], negReg: neg(presReg[3]) },
    voi:  { aff: voiAff,  neg: neg(pres[4]), affReg: presReg[4], negReg: neg(presReg[4]) },
    Loro: { aff: subj[5], neg: neg(subj[5]), affReg: subjReg[5], negReg: neg(subjReg[5]) },
  };
}

// ============================================================
// LETTER-LEVEL DIFF (language-agnostic; carried over verbatim)
// ============================================================
function diffMarkHTML(actual, regular) {
  if (actual === regular) return escapeHTML(actual);
  const a = actual, b = regular;
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const keep = new Array(m).fill(false);
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { keep[i - 1] = true; i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) i--;
    else j--;
  }
  let out = "", run = "", runIrr = false;
  const flush = () => {
    if (!run) return;
    out += runIrr ? `<span class="irregular">${escapeHTML(run)}</span>` : escapeHTML(run);
    run = "";
  };
  for (let k = 0; k < m; k++) {
    const irr = !keep[k];
    if (irr !== runIrr) { flush(); runIrr = irr; }
    run += a[k];
  }
  flush();
  return out;
}
function escapeHTML(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// ============================================================
// NON-FINITE FORMS + AUXILIARY
// ============================================================
const GER_EXACT = {
  fare: "facendo", dire: "dicendo", bere: "bevendo", porre: "ponendo",
  produrre: "producendo", tradurre: "traducendo", ridurre: "riducendo",
  proporre: "proponendo",
};
const PP_EXACT = {
  essere: "stato", stare: "stato", fare: "fatto", dire: "detto",
  prendere: "preso", mettere: "messo", scrivere: "scritto", leggere: "letto",
  vedere: "visto", vivere: "vissuto", venire: "venuto", nascere: "nato",
  morire: "morto", aprire: "aperto", offrire: "offerto", chiudere: "chiuso",
  rompere: "rotto", chiedere: "chiesto", rispondere: "risposto", rimanere: "rimasto",
  scegliere: "scelto", spegnere: "spento", correre: "corso", perdere: "perso",
  decidere: "deciso", ridere: "riso", muovere: "mosso", piacere: "piaciuto",
  conoscere: "conosciuto", bere: "bevuto", vincere: "vinto", spendere: "speso",
  spingere: "spinto", piangere: "pianto", dipingere: "dipinto", rendere: "reso",
  nascondere: "nascosto", togliere: "tolto", scendere: "sceso",
  succedere: "successo", accendere: "acceso", aggiungere: "aggiunto",
  raggiungere: "raggiunto", giungere: "giunto", concludere: "concluso",
  dividere: "diviso", risolvere: "risolto", svolgere: "svolto", stringere: "stretto",
  produrre: "prodotto", tradurre: "tradotto", ridurre: "ridotto", porre: "posto",
  proporre: "proposto", crescere: "cresciuto", esprimere: "espresso",
  soffrire: "sofferto", scoprire: "scoperto", trascorrere: "trascorso",
  comprendere: "compreso", descrivere: "descritto", permettere: "permesso",
  promettere: "promesso", smettere: "smesso", riconoscere: "riconosciuto",
  sorridere: "sorriso", sostenere: "sostenuto", possedere: "posseduto",
  dirigere: "diretto", fingere: "finto", distinguere: "distinto",
  // expansion-batch participles (participio = every compound tense)
  apparire: "apparso", assumere: "assunto", riassumere: "riassunto",
  concedere: "concesso", condividere: "condiviso", confondere: "confuso",
  convincere: "convinto", coprire: "coperto", scoprire: "scoperto",
  correggere: "corretto", corrispondere: "corrisposto", dipendere: "dipeso",
  escludere: "escluso", evadere: "evaso", friggere: "fritto",
  interrompere: "interrotto", intervenire: "intervenuto", avvenire: "avvenuto",
  introdurre: "introdotto", maledire: "maledetto", mordere: "morso",
  offendere: "offeso", opporre: "opposto", comporre: "composto",
  ottenere: "ottenuto", percorrere: "percorso", prevedere: "previsto",
  proteggere: "protetto", richiedere: "richiesto", riempire: "riempito",
  rompere: "rotto", sconfiggere: "sconfitto", soddisfare: "soddisfatto",
  sorgere: "sorto", sorprendere: "sorpreso", tacere: "taciuto",
  valere: "valso", volgere: "volto", rivolgere: "rivolto", accorgere: "accorto",
  assistere: "assistito", insistere: "insistito", resistere: "resistito",
  esistere: "esistito", concludere: "concluso", deludere: "deluso",
  mantenere: "mantenuto", sostenere: "sostenuto", possedere: "posseduto",
  raggiungere: "raggiunto", aggiungere: "aggiunto", giungere: "giunto",
  spegnere: "spento", discutere: "discusso", respingere: "respinto",
  piovere: "piovuto",
};
function gerund(infinitive) {
  const base = stripReflexive(infinitive);
  if (GER_EXACT[base]) return GER_EXACT[base];
  const { stem, type } = getStemAndType(base);
  return type === "are" ? stem + "ando" : stem + "endo";
}
function pastParticiple(infinitive) {
  const base = stripReflexive(infinitive);
  if (PP_EXACT[base]) return PP_EXACT[base];
  const { stem, type } = getStemAndType(base);
  if (type === "are") return stem + "ato";
  if (type === "ere") return stem + "uto";
  return stem + "ito"; // ire + ireIsco
}
function auxiliaryOf(infinitive) {
  if (/rsi$/.test(infinitive)) return "essere"; // reflexives
  return ESSERE_AUX.has(stripReflexive(infinitive)) ? "essere" : "avere";
}
function participles(infinitive) {
  return {
    gerund: gerund(infinitive),
    pastParticiple: pastParticiple(infinitive),
    auxiliary: auxiliaryOf(infinitive),
  };
}

// Compound tense builder: auxiliary (in the requested simple tense) + participio
// passato. Returns the masculine-singular default; with `essere` the participle
// agrees with the subject in real use. Exposed for the verb-detail
// "passato prossimo" row and the seeder.
const COMPOUND_AUX_TENSE = {
  passato_prossimo: "present",
  trapassato_prossimo: "imperfect",
  futuro_anteriore: "future",
  condizionale_passato: "conditional",
};
function compound(infinitive, compoundTense) {
  const aux = auxiliaryOf(infinitive);
  const auxTense = COMPOUND_AUX_TENSE[compoundTense] || "present";
  const auxForms = conjugate(aux, auxTense).forms;
  const pp = pastParticiple(infinitive);
  // With essere the participle agrees with the subject. We show the masculine
  // default: singular -o for io/tu/lui, plural -i for noi/voi/loro.
  return auxForms.map((a, idx) => {
    let p = pp;
    if (aux === "essere" && idx >= 3 && p.endsWith("o")) p = p.slice(0, -1) + "i";
    return `${a} ${p}`;
  });
}

const ALL_VERBS_LIST = buildVerbList();

export {
  ALL_VERBS, ALL_VERBS_LIST, TOP_50_VERBS, ADDITIONAL_VERBS, VOCABULARY, IRREGULAR_FORMS,
  PRONOUNS, TENSES, TENSE_LABELS,
  SUBJUNCTIVE_TENSES, SUBJUNCTIVE_LABELS,
  IMPERATIVE_ROWS, IMPERATIVE_LABELS,
  conjugate, conjugateSubj, imperative, getIrregularFlags, diffMarkHTML, buildVerbList,
  gerund, pastParticiple, participles, auxiliaryOf, compound,
};
